import './styles.sass';

import { cloneDeep, each } from 'lodash-es';

import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import config from 'util/site_config';
import NumberHelper from 'util/number_helper';
import MentionSourceProfileIcon from 'components/Explorer/ContentPanel/MentionSourceProfileIcon';
import * as QueryParams from 'components/Explorer/models/query_params';
import RecentMentions from './RecentMentions';
import useQueryParams from 'hooks/useQueryParams';

const MentionSource = ({ mentionSource }) => {
  const params = useQueryParams();
  const [recentMentionsVisible, setRecentMentionsVisible] = useState(false);

  const filtersForMentionSource = useMemo(() => {
    const filters = cloneDeep(params.filters);
    each(config.mentionsFilterParams, (key) => {
      delete filters[key];
      delete filters[key + '[]'];
    });
    if (filters.mention_sources_mentioned_after) filters.mentioned_after = filters.mention_sources_mentioned_after;
    if (filters.mention_sources_mentioned_before) filters.mentioned_before = filters.mention_sources_mentioned_before;
    filters['mention_sources[]'] = mentionSource.id;
    return filters;
  }, [params.filters, mentionSource.id]);

  const mentionsLink = useMemo(
    () => QueryParams.locationFor(QueryParams.withFilters(params, filtersForMentionSource), '/mentions'),
    [params, filtersForMentionSource]
  );

  const mentionSourceUrlOrName = () => {
    if (mentionSource.url) {
      return (
        <a href={mentionSource.url} target="_blank" rel="noreferrer" data-testid="url">
          {mentionSource.name}
        </a>
      );
    }
    return mentionSource.name;
  };

  const country = () => {
    if (!mentionSource.country) return null;
    return (
      <span className="country" data-testid="country">
        {mentionSource.country}
      </span>
    );
  };

  const followers = () => {
    if (mentionSource.profileType !== 'tw') return null;
    return (
      <div className="followers" data-testid="followers">
        <h3>{NumberHelper.formatStatistic(mentionSource.followers)}</h3>
        {I18n.t('Explorer.MentionSources.followers', { count: mentionSource.followers })}
      </div>
    );
  };

  const recentMentionsButton = () => {
    const text = recentMentionsVisible
      ? I18n.t('Explorer.MentionSources.MentionSource.hide_recent_mentions')
      : I18n.t('Explorer.MentionSources.MentionSource.show_recent_mentions');
    const className = recentMentionsVisible ? 'recent-mentions-button hide' : 'recent-mentions-button show';

    return (
      <button
        type="button"
        onClick={() => setRecentMentionsVisible((v) => !v)}
        className={className}
        aria-expanded={recentMentionsVisible}
      >
        {text}
      </button>
    );
  };

  return (
    <article key={mentionSource.id} className="Explorer-MentionSources-MentionSource">
      <div className="row">
        <MentionSourceProfileIcon profileType={mentionSource.profileType} />
        <div className="title">
          <h3>{mentionSourceUrlOrName()}</h3>
          <p>
            {I18n.t(`Explorer.Highlights.TopMentionSources.mention_source_types.${mentionSource.profileType}`)}
            {country()}
          </p>
        </div>
        <div className="counts">
          <h3>{NumberHelper.formatNumberWithDelimiter(mentionSource.mentions)}</h3>
          {I18n.t('Explorer.MentionSources.mentions_count', { count: mentionSource.mentions })}
        </div>
        <div className="counts">{followers()}</div>
        <aside aria-label={`Actions for ${mentionSource.name}`}>
          <Link className="mentions-link-button" to={mentionsLink}>
            {I18n.t('Explorer.MentionSources.mentions_button', { count: mentionSource.mentions })}
          </Link>
          {recentMentionsButton()}
        </aside>
      </div>
      <RecentMentions
        visible={recentMentionsVisible}
        key={mentionSource.id}
        mentionSource={mentionSource}
        mentionsLink={mentionsLink}
        filtersForMentionSource={filtersForMentionSource}
      />
    </article>
  );
};

MentionSource.propTypes = {
  mentionSource: PropTypes.object.isRequired
};

export default MentionSource;
